/**
 * Node Analytics SDK
 * Minimal analytics system with batching, PII sanitization, and retry logic.
 */

import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { getSessionId, generateRequestId } from './session';

// PII fields to NEVER send
const PII_FIELDS = new Set([
  'email',
  'phone',
  'name',
  'firstName',
  'lastName',
  'note',
  'notes',
  'address',
  'value',
  'fieldValue',
  'oldValue',
  'newValue',
  'content',
  'description',
  'comment',
  'message',
  'password',
  'ssn',
  'sin',
  'creditCard',
  'cardNumber',
]);

// Batching config
const FLUSH_INTERVAL_MS = 2000;
const FLUSH_THRESHOLD = 25;

// Retry config
const RETRY_DELAYS = [500, 1500, 3000];

/**
 * Deep sanitize an object, removing PII fields.
 * Returns the sanitized object and a list of redaction flags.
 * @param {Object} obj - Object to sanitize
 * @returns {{ sanitized: Object, flags: string[] }}
 */
function sanitizeObject(obj) {
  if (!obj || typeof obj !== 'object') {
    return { sanitized: obj, flags: [] };
  }

  const flags = [];

  function recurse(current) {
    if (Array.isArray(current)) {
      return current.map(recurse);
    }

    if (current && typeof current === 'object') {
      const result = {};
      for (const key of Object.keys(current)) {
        const lowerKey = key.toLowerCase();
        if (PII_FIELDS.has(lowerKey) || PII_FIELDS.has(key)) {
          flags.push(`removed_${key}`);
          // Skip this key entirely
        } else {
          result[key] = recurse(current[key]);
        }
      }
      return result;
    }

    return current;
  }

  const sanitized = recurse(obj);
  return { sanitized, flags: [...new Set(flags)] };
}

/**
 * Checks if analytics should be enabled based on current domain.
 * Only sends events from osiris.usenode.com
 * @returns {boolean}
 */
function isAnalyticsEnabled() {
  if (typeof window === 'undefined') return false;
  return window.location?.hostname === 'osiris.usenode.com';
}

/**
 * Creates the Node Analytics instance.
 * @param {{ clientVersion?: string }} options
 * @returns {{ track: Function, flush: Function }}
 */
export function createNodeAnalytics({ clientVersion } = {}) {
  // Only enable analytics on osiris.usenode.com
  if (!isAnalyticsEnabled()) {
    return {
      track: () => {},
      flush: () => Promise.resolve(),
    };
  }

  let buffer = [];
  let flushTimer = null;
  let isFlushing = false;

  const sessionId = getSessionId();
  const resolvedClientVersion =
    clientVersion || process.env.REACT_APP_VERSION || '0.1.0';

  /**
   * Schedules a flush after FLUSH_INTERVAL_MS.
   */
  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flush();
    }, FLUSH_INTERVAL_MS);
  }

  /**
   * Sends events with retry logic.
   * @param {Array} events - Events to send
   * @param {number} attempt - Current attempt index
   * @returns {Promise<boolean>} - Success status
   */
  async function sendWithRetry(events, attempt = 0) {
    const requestId = generateRequestId();

    try {
      const response = await nodeAxiosFirebase({
        method: 'POST',
        url: 'eventsAnalytics',
        body: events,
        headers: {
          'x-session-id': sessionId,
          'x-request-id': requestId,
          'x-client-version': resolvedClientVersion,
          'x-source': 'web',
        },
      });

      // Check for error response
      if (response?.errorData) {
        throw new Error(response.errorData.message || 'Request failed');
      }

      return true;
    } catch (error) {
      if (attempt < RETRY_DELAYS.length) {
        const delay = RETRY_DELAYS[attempt];
        await new Promise((resolve) => setTimeout(resolve, delay));
        return sendWithRetry(events, attempt + 1);
      }

      // All retries exhausted
      console.warn(
        '[NodeAnalytics] Failed to send events after retries:',
        error?.message || error
      );
      return false;
    }
  }

  /**
   * Flushes the buffer, sending all queued events.
   * @returns {Promise<void>}
   */
  async function flush() {
    if (isFlushing || buffer.length === 0) return;

    isFlushing = true;
    const eventsToSend = [...buffer];
    buffer = [];

    // Clear timer if exists
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }

    const success = await sendWithRetry(eventsToSend);

    if (!success) {
      // Re-queue events at the front of the buffer for next flush attempt
      buffer = [...eventsToSend, ...buffer];
      // Schedule next flush attempt
      scheduleFlush();
    }

    isFlushing = false;
  }

  /**
   * Tracks an analytics event.
   * @param {{
   *   eventName: string,
   *   eventVersion?: string,
   *   timeStamp?: string,
   *   context?: Object,
   *   payload?: Object
   * }} event
   */
  function track(event) {
    if (!event?.eventName) {
      console.warn('[NodeAnalytics] Event requires eventName');
      return;
    }

    // Sanitize context and payload
    const { sanitized: sanitizedContext, flags: contextFlags } = sanitizeObject(
      event.context || {}
    );
    const { sanitized: sanitizedPayload, flags: payloadFlags } = sanitizeObject(
      event.payload || {}
    );

    const redactionFlags = [...contextFlags, ...payloadFlags];
    const containsPII = redactionFlags.length > 0;

    const finalEvent = {
      eventName: event.eventName,
      eventVersion: event.eventVersion || '1.0',
      timeStamp: event.timeStamp || new Date().toISOString(),
      context: sanitizedContext,
      payload: sanitizedPayload,
      privacy: { containsPII },
      redaction_flags: redactionFlags,
      meta: {
        sessionId,
        clientVersion: resolvedClientVersion,
        source: 'web',
      },
    };

    buffer.push(finalEvent);

    // Flush immediately if threshold reached
    if (buffer.length >= FLUSH_THRESHOLD) {
      flush();
    } else {
      scheduleFlush();
    }
  }

  // Flush on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (buffer.length > 0) {
        // Use sendBeacon for reliability on unload
        try {
          const requestId = generateRequestId();
          const blob = new Blob([JSON.stringify(buffer)], {
            type: 'application/json',
          });
          navigator.sendBeacon(
            `https://api.usenode.com/events?sessionId=${sessionId}&requestId=${requestId}`,
            blob
          );
        } catch {
          // Fallback: sync flush
          flush();
        }
      }
    });
  }

  return { track, flush };
}

// Default instance (lazy singleton)
let defaultInstance = null;

/**
 * Gets the default analytics instance.
 * @returns {{ track: Function, flush: Function }}
 */
export function getAnalytics() {
  if (!defaultInstance) {
    defaultInstance = createNodeAnalytics();
  }
  return defaultInstance;
}

/**
 * Collects basic browser/device info for session.started event
 * @returns {Object} - Device info (no PII)
 */
function getDeviceInfo() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {};
  }

  return {
    screenWidth: window.screen?.width || null,
    screenHeight: window.screen?.height || null,
    viewportWidth: window.innerWidth || null,
    viewportHeight: window.innerHeight || null,
    language: navigator.language || null,
    platform: navigator.platform || null,
    userAgent: navigator.userAgent?.substring(0, 200) || null, // Truncate for safety
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || null,
    isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    isOnline: navigator.onLine ?? true,
  };
}

/**
 * Initializes the default analytics instance with custom options.
 * Call this once at app startup if you need custom configuration.
 * @param {{ clientVersion?: string }} options
 */
export function initAnalytics(options = {}) {
  defaultInstance = createNodeAnalytics(options);

  // Send session.started event
  try {
    const deviceInfo = getDeviceInfo();
    const referrer = typeof document !== 'undefined' ? document.referrer : null;
    const currentUrl = typeof window !== 'undefined' ? window.location?.pathname : null;

    defaultInstance.track({
      eventName: 'session.started',
      context: {
        view: 'app_init',
        layoutScope: 'app',
      },
      payload: {
        ...deviceInfo,
        referrer: referrer?.substring(0, 500) || null, // Truncate for safety
        entryPage: currentUrl,
      },
    });
  } catch (error) {
    console.warn('[NodeAnalytics] Failed to track session.started:', error);
  }

  return defaultInstance;
}

export default {
  createNodeAnalytics,
  getAnalytics,
  initAnalytics,
};

